<template>
  <b-dropdown
    class="fu-dl-menu"
    variant="outline-secondary"
    size="sm"
    toggle-class="fu-dl-menu__toggle"
    no-caret
    right
  >
    <template #button-content>
      <i class="fa fa-download fu-dl-menu__icon" aria-hidden="true" />
      <span class="fu-dl-menu__label">Download</span>
    </template>

    <b-dropdown-item
      :disabled="!hasRows"
      @click="triggerPrint"
    >
      <i class="fa fa-print fu-dl-menu__item-icon" aria-hidden="true" />
      Print
    </b-dropdown-item>
    <b-dropdown-item
      :disabled="!hasRows"
      @click="triggerCsv"
    >
      <i class="fa fa-file-text-o fu-dl-menu__item-icon" aria-hidden="true" />
      Download CSV
    </b-dropdown-item>
  </b-dropdown>
</template>

<script>
/**
 * ListDownloadMenu — Download button for the Overdue Recall and Unscheduled Treatment pages.
 *
 * Renders a b-dropdown with two items: Print (opens a clean HTML view in a new tab and
 * invokes window.print()) and Download CSV (triggers a UTF-8 BOM CSV file download).
 *
 * Props:
 *   queue   String  'overdueRecall' | 'unscheduledTreatment'
 *   rows    Array   the page-level computed array (overdueRecallData / unscheduledTreatmentData)
 *   title   String  human label shown in the print header, e.g. 'Overdue Recall'
 *   ctx     Object  { dateFrom, dateTo, dateFormatStr, currencySymbol, remindersPaidFor, siteName }
 *
 * Disable behaviour: both items are disabled when rows is empty. The dropdown toggle stays
 * enabled so the user gets visual feedback about why the items are greyed.
 *
 * PHI masking: rows are already masked at the data boundary — no masking here.
 */
import moment from 'moment-timezone';
import {
  buildExportMatrix,
  toCsv,
  buildPrintHtml,
  exportFilename,
} from '@/utils/followups/listExport.js';

export default {
  name: 'ListDownloadMenu',

  props: {
    queue: {
      type: String,
      required: true,
    },
    rows: {
      type: Array,
      default: () => [],
    },
    title: {
      type: String,
      default: '',
    },
    ctx: {
      type: Object,
      default: () => ({}),
    },
  },

  computed: {
    hasRows() {
      return Array.isArray(this.rows) && this.rows.length > 0;
    },

    /** YYYY-MM-DD string for the 'from' date (for filename + print meta). */
    ymdFrom() {
      return this._toYmd(this.ctx && this.ctx.dateFrom);
    },

    /** YYYY-MM-DD string for the 'to' date. */
    ymdTo() {
      return this._toYmd(this.ctx && this.ctx.dateTo);
    },

    /** Human-readable date range label for the print header. */
    dateRangeLabel() {
      const fmt = (ymd) => {
        if (!ymd) return '';
        const m = moment(ymd, 'YYYY-MM-DD');
        return m.isValid() ? m.format('MMM D, YYYY') : ymd;
      };
      const f = fmt(this.ymdFrom);
      const t = fmt(this.ymdTo);
      if (f && t) return `${f} – ${t}`;
      return f || t || '';
    },

    /** The meta line shown beneath the title in the print view. */
    metaLine() {
      const parts = [];
      if (this.ctx && this.ctx.siteName) parts.push(this.ctx.siteName);
      if (this.dateRangeLabel) parts.push(this.dateRangeLabel);
      const generated = `Generated ${moment().format('MMM D, YYYY [at] h:mm A')}`;
      parts.push(generated);
      return parts.join(' · ');
    },

    /** The export ctx passed to the pure util (normalised). */
    exportCtx() {
      const c = this.ctx || {};
      return {
        dateFormatStr: c.dateFormatStr || 'MM/DD/YYYY',
        currencySymbol: c.currencySymbol || '$',
        remindersPaidFor: !!c.remindersPaidFor,
      };
    },
  },

  methods: {
    _toYmd(value) {
      if (!value) return '';
      if (typeof value === 'string') {
        // 'MM/DD/YY' (init format used by the pages) or ISO-like
        const patterns = ['MM/DD/YY', 'YYYY-MM-DD', 'MM/DD/YYYY'];
        for (const p of patterns) {
          const m = moment(value, p, true);
          if (m.isValid()) return m.format('YYYY-MM-DD');
        }
      }
      // Date object
      const m = moment(value);
      return m.isValid() ? m.format('YYYY-MM-DD') : '';
    },

    triggerCsv() {
      const { headers, rows } = buildExportMatrix(this.queue, this.rows, this.exportCtx);
      const csvStr = toCsv(headers, rows);
      const filename = exportFilename(
        this.queue,
        { from: this.ymdFrom, to: this.ymdTo },
        'csv',
      );
      // Blob + temp anchor pattern (same as SuperAdmin.vue:13203)
      const blob = new Blob([csvStr], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },

    triggerPrint() {
      const { headers, rows } = buildExportMatrix(this.queue, this.rows, this.exportCtx);
      const html = buildPrintHtml(this.title, this.metaLine, headers, rows);
      const w = window.open('', '_blank');
      if (w) {
        w.document.write(html);
        w.document.close();
        w.focus();
        w.print();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.fu-dl-menu {
  &__toggle {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 13px;
    font-weight: 500;
  }

  &__icon {
    font-size: 13px;
  }

  &__label {
    font-size: 13px;
  }

  &__item-icon {
    margin-right: 6px;
    width: 14px;
    text-align: center;
  }
}

// Align the toggle styling with the FollowupsFilterButton secondary style
::v-deep .fu-dl-menu__toggle.btn {
  border-radius: 8px;
  border-color: #d1d5db;
  color: #374151;
  background: #fff;
  padding: 6px 12px;

  &:hover,
  &:focus {
    background: #f9fafb;
    border-color: #9ca3af;
  }
}
</style>
